import React from 'react';
import { logError } from 'Helpers/errors';
import { Alert } from 'pounceV2';

interface ErrorBoundaryProps {
  fallbackStrategy?: 'passthrough' | 'invisible' | 'placeholder';
  fallbackComponent?: React.ReactNode;
  children?: React.ReactNode;
  /** When this value changes, the error state resets so children render again. */
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

const DefaultFallbackComponent: React.FC = () => (
  <Alert
    title="Something went wrong and we could not correctly display this content"
    variant="warning"
    variantBackgroundStyle="transparent"
  />
);

class ErrorBoundaryV3 extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static defaultProps: Partial<ErrorBoundaryProps>;

  state = { hasError: false };

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, { componentStack }: React.ErrorInfo) {
    // Display fallback UI
    this.setState({ hasError: true });

    // send this error to our logging service.
    logError(error, { extras: { react: componentStack } });
  }

  render() {
    const { fallbackStrategy, fallbackComponent, children } = this.props;

    // if no error occurs -> render as normal
    if (!this.state.hasError) {
      return children;
    }

    // else decide what to show based on the selected strategy
    switch (fallbackStrategy) {
      case 'invisible':
        return null;
      case 'placeholder':
        return fallbackComponent;
      case 'passthrough':
      default:
        return children;
    }
  }
}

ErrorBoundaryV3.defaultProps = {
  fallbackStrategy: 'placeholder',
  fallbackComponent: <DefaultFallbackComponent />,
};

export default ErrorBoundaryV3;
